import React, { useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { toast } from 'sonner';
import { ChevronDown, ChevronUp, Edit2, Save, X, Plus, Trash2 } from 'lucide-react';

const TaskCard = ({ target, onUpdate }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [editedTitle, setEditedTitle] = useState(target.name);
  const [tasks, setTasks] = useState(target.tasks || []);
  const [editedPriority, setEditedPriority] = useState(target.priority || '');
  const [editedDeadline, setEditedDeadline] = useState(target.deadline_date || '');

  console.log('TaskCard rendered for:', target.name, 'tasks:', tasks.length);

  // Initialize tasks if empty
  const initializeTasks = () => {
    if (tasks.length === 0) {
      const newTasks = Array.from({ length: 10 }, (_, index) => ({
        id: `task_${Date.now()}_${index}`,
        serial: index + 1,
        name: '',
        target_date: '',
        assigned_to: '',
        notes: '',
        completed: false
      }));
      setTasks(newTasks);
      return newTasks;
    }
    return tasks;
  };

  // Calculate progress
  const calculateProgress = () => {
    const currentTasks = tasks.length > 0 ? tasks : initializeTasks();
    const completedCount = currentTasks.filter(task => task.completed).length;
    return (completedCount / 10) * 100;
  };

  const progress = calculateProgress();
  const completedCount = tasks.filter(task => task.completed).length;

  // Calculate days remaining to deadline
  const calculateDaysRemaining = () => {
    if (!target.deadline_date) return null;
    const today = new Date();
    const deadline = new Date(target.deadline_date);
    const diffTime = deadline - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const daysRemaining = calculateDaysRemaining();

  // Handle task field update
  const handleTaskUpdate = (taskId, field, value) => {
    const updatedTasks = tasks.map(task =>
      task.id === taskId ? { ...task, [field]: value } : task
    );
    setTasks(updatedTasks);
  };

  // Handle task completion toggle
  const handleToggleComplete = (taskId) => {
    const updatedTasks = tasks.map(task =>
      task.id === taskId ? { ...task, completed: !task.completed } : task
    );
    setTasks(updatedTasks);
    saveTasks(updatedTasks);
  };

  // Save tasks to backend
  const saveTasks = async (updatedTasks = tasks) => {
    try {
      await onUpdate(target.id, { tasks: updatedTasks });
      toast.success('Tasks updated successfully');
    } catch (error) {
      toast.error('Failed to update tasks');
    }
  };

  // Save title
  const saveTitle = async () => {
    try {
      await onUpdate(target.id, { name: editedTitle });
      setIsEditingTitle(false);
      toast.success('Title updated successfully');
    } catch (error) {
      toast.error('Failed to update title');
    }
  };

  // Save priority and deadline
  const saveProjectDetails = async () => {
    try {
      const updates = {};
      if (editedPriority !== target.priority) {
        updates.priority = editedPriority ? parseInt(editedPriority) : null;
      }
      if (editedDeadline !== target.deadline_date) {
        updates.deadline_date = editedDeadline;
      }
      
      if (Object.keys(updates).length > 0) {
        await onUpdate(target.id, updates);
        toast.success('Project details updated successfully');
      }
    } catch (error) {
      toast.error('Failed to update project details');
    }
  };

  // Expand/collapse card
  const toggleExpand = (e) => {
    // Prevent event bubbling
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    
    console.log('TaskCard: Toggle expand clicked', isExpanded);
    
    if (!isExpanded && tasks.length === 0) {
      const initialTasks = initializeTasks();
      saveTasks(initialTasks);
    }
    setIsExpanded(!isExpanded);
  };

  return (
    <div className="bg-white rounded-xl shadow-md hover:shadow-xl transition-all p-6 border border-slate-200">
      {/* Card Header */}
      <div className="flex justify-between items-start mb-4">
        <div className="flex-1">
          <div className="flex items-center gap-3">
            {/* Priority Badge */}
            {target.priority && (
              <span className="inline-flex items-center justify-center w-8 h-8 bg-indigo-600 text-white font-bold rounded-full text-sm">
                #{target.priority}
              </span>
            )}
            {isEditingTitle ? (
              <div className="flex items-center gap-2 flex-1">
                <Input
                  type="text"
                  value={editedTitle}
                  onChange={(e) => setEditedTitle(e.target.value)}
                  className="text-lg font-bold"
                  autoFocus
                />
                <Button onClick={saveTitle} size="sm" variant="ghost">
                  <Save className="w-4 h-4" />
                </Button>
                <Button onClick={() => {
                  setEditedTitle(target.name);
                  setIsEditingTitle(false);
                }} size="sm" variant="ghost">
                  <X className="w-4 h-4" />
                </Button>
              </div>
            ) : (
              <div className="flex items-center gap-2 flex-1">
                <h3 className="text-lg font-bold text-slate-800">{target.name}</h3>
                <Button onClick={() => setIsEditingTitle(true)} size="sm" variant="ghost">
                  <Edit2 className="w-3 h-3" />
                </Button>
              </div>
            )}
          </div>
          
          <div className="mt-2 flex items-center gap-4 text-sm">
            <span className="text-slate-600">
              {completedCount} of 10 tasks completed
            </span>
            
            {/* Deadline and Days Remaining */}
            {target.deadline_date && (
              <>
                <span className="text-slate-400">•</span>
                <span className="text-slate-600">
                  📅 Deadline: <span className="font-semibold">{target.deadline_date}</span>
                </span>
                {daysRemaining !== null && (
                  <>
                    <span className="text-slate-400">•</span>
                    <span className={`font-semibold ${
                      daysRemaining < 0 ? 'text-red-600' : 
                      daysRemaining <= 7 ? 'text-orange-600' : 
                      daysRemaining <= 30 ? 'text-yellow-600' : 
                      'text-green-600'
                    }`}>
                      {daysRemaining < 0 ? `${Math.abs(daysRemaining)} days overdue` : 
                       daysRemaining === 0 ? 'Due today!' :
                       `${daysRemaining} days remaining`}
                    </span>
                  </>
                )}
              </>
            )}
          </div>
        </div>
        <Button
          onClick={(e) => toggleExpand(e)}
          size="sm"
          variant="ghost"
          className="ml-2"
          type="button"
        >
          {isExpanded ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
        </Button>
      </div>

      {/* Progress Bar */}
      <div className="mb-4">
        <div className="flex justify-between text-sm mb-1">
          <span className="text-slate-600">Progress</span>
          <span className="font-semibold text-slate-800">{progress.toFixed(0)}%</span>
        </div>
        <div className="w-full bg-slate-200 rounded-full h-3">
          <div
            className="bg-gradient-to-r from-green-500 to-emerald-600 h-3 rounded-full transition-all"
            style={{ width: `${progress}%` }}
          />
        </div>
      </div>

      {/* Expanded Task List */}
      {isExpanded && (
        <div className="mt-6 space-y-3 border-t pt-4">
          {/* Project Details Section */}
          <div className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-lg p-4 mb-4">
            <h4 className="font-semibold text-slate-700 mb-3">Project Details</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-600 mb-1">
                  Priority Number
                </label>
                <Input
                  type="number"
                  value={editedPriority}
                  onChange={(e) => setEditedPriority(e.target.value)}
                  placeholder="Enter priority (e.g., 1, 2, 3...)"
                  className="w-full"
                  min="1"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-600 mb-1">
                  Target Date
                </label>
                <Input
                  type="date"
                  value={editedDeadline}
                  onChange={(e) => setEditedDeadline(e.target.value)}
                  className="w-full"
                />
              </div>
            </div>
            <div className="flex justify-end mt-3">
              <Button onClick={saveProjectDetails} size="sm" variant="default">
                <Save className="w-4 h-4 mr-1" /> Save Project Details
              </Button>
            </div>
          </div>

          <div className="flex justify-between items-center mb-4">
            <h4 className="font-semibold text-slate-700">Task List</h4>
            <Button onClick={() => saveTasks()} size="sm">
              <Save className="w-4 h-4 mr-1" /> Save All Tasks
            </Button>
          </div>

          {/* Task Headers */}
          <div className="grid grid-cols-12 gap-2 text-xs font-semibold text-slate-600 border-b pb-2">
            <div className="col-span-1">#</div>
            <div className="col-span-3">Task Name</div>
            <div className="col-span-2">Target Date</div>
            <div className="col-span-2">Assigned To</div>
            <div className="col-span-3">Notes</div>
            <div className="col-span-1 text-center">Done</div>
          </div>

          {/* Task Rows */}
          {(tasks.length > 0 ? tasks : initializeTasks()).map((task) => (
            <div
              key={task.id}
              className={`grid grid-cols-12 gap-2 items-center py-2 border-b border-slate-100 ${
                task.completed ? 'opacity-60 bg-green-50' : ''
              }`}
            >
              {/* Serial Number */}
              <div className="col-span-1">
                <span className={`text-sm font-medium ${task.completed ? 'line-through' : ''}`}>
                  {task.serial}
                </span>
              </div>

              {/* Task Name */}
              <div className="col-span-3">
                <Input
                  type="text"
                  value={task.name}
                  onChange={(e) => handleTaskUpdate(task.id, 'name', e.target.value)}
                  placeholder="Enter task name..."
                  className={`text-sm ${task.completed ? 'line-through' : ''}`}
                />
              </div>

              {/* Target Date */}
              <div className="col-span-2">
                <Input
                  type="date"
                  value={task.target_date}
                  onChange={(e) => handleTaskUpdate(task.id, 'target_date', e.target.value)}
                  className={`text-sm ${task.completed ? 'line-through' : ''}`}
                />
              </div>

              {/* Assigned To */}
              <div className="col-span-2">
                <Input
                  type="text"
                  value={task.assigned_to}
                  onChange={(e) => handleTaskUpdate(task.id, 'assigned_to', e.target.value)}
                  placeholder="Assign to..."
                  className={`text-sm ${task.completed ? 'line-through' : ''}`}
                />
              </div>

              {/* Notes */}
              <div className="col-span-3">
                <Input
                  type="text"
                  value={task.notes || ''}
                  onChange={(e) => handleTaskUpdate(task.id, 'notes', e.target.value)}
                  placeholder="Add notes..."
                  className={`text-sm ${task.completed ? 'line-through' : ''}`}
                />
              </div>

              {/* Complete Checkbox */}
              <div className="col-span-1 flex justify-center">
                <input
                  type="checkbox"
                  checked={task.completed}
                  onChange={() => handleToggleComplete(task.id)}
                  className="w-5 h-5 rounded border-slate-300 text-green-600 focus:ring-green-500 cursor-pointer"
                />
              </div>
            </div>
          ))}

          <div className="flex justify-end gap-2 mt-4 pt-4 border-t">
            <Button onClick={() => saveTasks()} size="sm">
              <Save className="w-4 h-4 mr-1" /> Save All Changes
            </Button>
            <Button onClick={() => setIsExpanded(false)} size="sm" variant="outline">
              Close
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default TaskCard;
